import { buildUrl, getToken } from '@/lib/api';
import { expectedJsonError, parseResponseText, staticApkFetchError } from './response';
import type { ApkReportDiff, ApkReportMeta, ApkSizeReport, DecompileFileContent, DecompileJobStatus, DecompileMode, DecompileView, SizeNode } from './types';

async function jsonFetch<T>(input: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('x-adh-token', token);
  if (init.body && !headers.has('Content-Type') && !(init.body instanceof Blob)) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(input, { credentials: 'include', ...init, headers });
  const text = await res.text();
  const parsed = parseResponseText(text, res.headers.get('content-type'));
  if (!res.ok) {
    throw staticApkFetchError(input, res.status, parsed);
  }
  if (!parsed.isJson) throw expectedJsonError(input, parsed);
  return parsed.data as T;
}

export const staticApkApi = {
  analyze(input: { hubPath: string; sourceDeviceSerial?: string }): Promise<{ reportId: string; report: ApkSizeReport; meta: ApkReportMeta }> {
    return jsonFetch(buildUrl('/api/static-analysis/apk/analyze'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  listReports(): Promise<{ reports: ApkReportMeta[] }> {
    return jsonFetch(buildUrl('/api/static-analysis/apk/reports'));
  },
  getReport(reportId: string): Promise<ApkSizeReport> {
    return jsonFetch(buildUrl(`/api/static-analysis/apk/reports/${encodeURIComponent(reportId)}`));
  },
  deleteReport(reportId: string): Promise<{ ok: boolean }> {
    return jsonFetch(buildUrl(`/api/static-analysis/apk/reports/${encodeURIComponent(reportId)}`), {
      method: 'DELETE',
    });
  },
  deleteReports(input: { reportIds?: string[]; sha256?: string }): Promise<{ removed: string[] }> {
    return jsonFetch(buildUrl('/api/static-analysis/apk/reports/delete-batch'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  diff(input: { beforeReportId: string; afterReportId: string }): Promise<{ diff: ApkReportDiff }> {
    return jsonFetch(buildUrl('/api/static-analysis/apk/diff'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  exportUrl(reportId: string, format: 'json' | 'html' | 'csv'): string {
    return buildUrl(`/api/static-analysis/apk/reports/${encodeURIComponent(reportId)}/export`, { format });
  },
  decompileStart(reportId: string, mode: DecompileMode = 'both'): Promise<{ status: DecompileJobStatus }> {
    return jsonFetch(buildUrl('/api/static-analysis/apk/decompile/start'), {
      method: 'POST',
      body: JSON.stringify({ reportId, mode }),
    });
  },
  decompileStatus(reportId: string): Promise<{ status: DecompileJobStatus }> {
    return jsonFetch(buildUrl(`/api/static-analysis/apk/decompile/${encodeURIComponent(reportId)}/status`));
  },
  decompileTree(reportId: string, view: DecompileView): Promise<{ tree: SizeNode }> {
    return jsonFetch(buildUrl(`/api/static-analysis/apk/decompile/${encodeURIComponent(reportId)}/tree`, { view }));
  },
  decompileSearch(reportId: string, q: string, view: DecompileView = 'raw'): Promise<{ results: SizeNode[] }> {
    return jsonFetch(buildUrl(`/api/static-analysis/apk/decompile/${encodeURIComponent(reportId)}/search`, { q, view }));
  },
  decompileFile(reportId: string, path: string, view: DecompileView = 'raw'): Promise<{ file: DecompileFileContent }> {
    return jsonFetch(buildUrl(`/api/static-analysis/apk/decompile/${encodeURIComponent(reportId)}/file`, { path, view }));
  },
};
